import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Divider,
  Icon,
  Typography,
  VStack,
} from '@vivaahealth/design-system';

import Row from '../../../components/Row';
import TextData from '../../../components/TextData';

import * as api from '../../../api';
import { constructAddress } from '../../../utils/practice';
import { maybeFormatNumberToNational } from '../../../utils/phoneNumber';
import { GroupedData } from '../../../utils/review';

interface GuarantorsProps {
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
}

interface GuarantorData {
  value: string | null;
  label: string;
}

export function getGuarantorConfiguration(
  guarantor: api.Guarantor.PrevisitGuarantor,
) {
  const {
    address,
    phoneNumberHome,
    phoneNumberMobile,
    phoneNumberWork,
    emailAddress,
    relationshipToPatient,
    sex,
  } = guarantor;
  const addressDisplay = address && constructAddress(address);
  const phoneNumber = phoneNumberMobile || phoneNumberHome || phoneNumberWork;
  const phoneNumberDisplay =
    phoneNumber && maybeFormatNumberToNational(phoneNumber);
  const relationship =
    relationshipToPatient &&
    api.Previsit.relationshipOptionsMap[relationshipToPatient];
  const sexDisplay = sex && api.Previsit.sexOptionsMap[sex];

  const selfGuarantorConfiguration: GroupedData<GuarantorData>[] = [
    {
      data: [
        {
          value: relationship,
          label: 'guarantorRelationshipToPatient',
        },
      ],
      Icon: Icon.User,
    },
  ];
  const otherGuarantorConfiguration: GroupedData<GuarantorData>[] = [
    {
      data: [
        {
          value: phoneNumberDisplay,
          label: 'guarantorPhoneNumber',
        },
      ],
      Icon: Icon.Phone,
    },
    {
      data: [
        {
          value: emailAddress,
          label: 'guarantorEmail',
        },
      ],
      Icon: Icon.Mail,
    },
    {
      data: [
        {
          value: addressDisplay,
          label: 'guarantorMailingAddress',
        },
      ],
      Icon: Icon.MapPin,
    },
    {
      data: [
        {
          value: sexDisplay,
          label: 'guarantorSex',
        },
      ],
      Icon: Icon.Globe,
    },
  ];

  const guarantorConfiguration: GroupedData<GuarantorData>[] = [
    ...(guarantor.relationshipToPatient !== api.Common.Relationship.Self
      ? otherGuarantorConfiguration
      : []),
    ...selfGuarantorConfiguration,
  ];

  return guarantorConfiguration;
}

export function cleanGuarantorConfiguration(
  guarantorConfiguration: GroupedData<GuarantorData>[],
) {
  const cleanedConfiguration = guarantorConfiguration.reduce(
    (acc: GroupedData<GuarantorData>[], groupedData) => {
      const data = groupedData.data.filter(({ value }) => Boolean(value));

      if (data.length) {
        acc.push({ ...groupedData, data });
      }

      return acc;
    },
    [],
  );

  return cleanedConfiguration;
}

const Guarantors = ({ field }: GuarantorsProps) => {
  const { t } = useTranslation('review');

  const {
    guarantors,
    selectedGuarantorId,
  }: api.Guarantor.PrevisitGuarantorFormFieldValue = JSON.parse(field.value);

  const selectedGuarantor = guarantors.find(
    (g) => g.id === selectedGuarantorId,
  );

  if (!guarantors || !selectedGuarantor) {
    return <Alert variant="danger">{t('guarantorNotFound')}</Alert>;
  }

  const name = [selectedGuarantor.firstName, selectedGuarantor.lastName]
    .filter(Boolean)
    .join(' ');

  const guarantorConfiguration = getGuarantorConfiguration(selectedGuarantor);
  const cleanedConfiguration = cleanGuarantorConfiguration(
    guarantorConfiguration,
  );

  return (
    <VStack gap={16}>
      {name && (
        <Typography bold variant="headline-small">
          {name}
        </Typography>
      )}
      {cleanedConfiguration.map(({ data, Icon, columns = 1 }, groupIndex) => {
        return (
          <React.Fragment key={JSON.stringify(data)}>
            <Row Icon={Icon as React.ComponentType<any>}>
              {data.map(({ label, value }) => (
                <TextData
                  key={label}
                  label={t(label)}
                  value={t(value as string)}
                />
              ))}
            </Row>
            {groupIndex !== cleanedConfiguration.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </VStack>
  );
};

export default Guarantors;
